import { NavLink, useLocation } from 'react-router-dom'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Card } from '@/shared/ui/Card'
import { StatCard } from '@/shared/ui/StatCard'
import { Avatar } from '@/shared/ui/Avatar'
import { Wallet, Loader2, Banknote } from 'lucide-react'
import { formatCurrency, formatDate } from '@/shared/utils'
import { useCollectorCashInHand, useCollectorCollectedList } from '@/api/hooks'

const TABS = [
  { label:'Overview',       path:'/dashboard/collector/overview',      key:'overview' },
  { label:'Cash In Hand',   path:'/dashboard/collector/cash-in-hand',  key:'cash-in-hand' },
  { label:'Collected List', path:'/dashboard/collector/collected-list',key:'collected-list' },
]

interface CashRow { id:number; trainer_name?:string; name?:string; cash_in_hand?:number; amount?:number; collected_at?:string; created_at?:string }

export function CollectorPage() {
  const location = useLocation()
  const tab = TABS.find(t => location.pathname.includes(t.key))?.key ?? 'overview'

  const cashQ      = useCollectorCashInHand(tab !== 'collected-list' ? {} : undefined)
  const collectedQ = useCollectorCollectedList(tab === 'collected-list' ? {} : undefined)

  const cashRows: CashRow[]      = cashQ.data?.data ?? cashQ.data ?? []
  const collectedRows: CashRow[] = collectedQ.data?.data ?? collectedQ.data ?? []
  const totalInHand = cashRows.reduce((s,r)=>s + (r.cash_in_hand ?? r.amount ?? 0), 0)

  return (
    <div className="space-y-5">
      <PageHeader title="Collector" breadcrumbs={[{label:'Dashboard',to:'/dashboard'},{label:'Collector'}]}/>
      <div className="flex border-b border-gray-100 bg-white rounded-t-xl overflow-x-auto">
        {TABS.map(t => <NavLink key={t.path} to={t.path}
          className={({isActive})=>`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${isActive?'border-brand-500 text-brand-600':'border-transparent text-gray-500 hover:text-gray-700'}`}>
          {t.label}
        </NavLink>)}
      </div>

      {tab === 'overview' && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatCard title="Trainers w/ Cash" value={cashRows.length} icon={<Wallet size={18}/>} color="amber"/>
          <StatCard title="Total Cash In Hand" value={formatCurrency(totalInHand)} icon={<Banknote size={18}/>} color="green"/>
        </div>
      )}

      {tab === 'collected-list' ? (
        <Card padding={false}>
          {collectedQ.isLoading ? <Center/> : (
            <div className="overflow-x-auto">
            <table className="drivys-table w-full">
              <thead className="bg-gray-50"><tr><th>Trainer</th><th>Amount</th><th>Collected</th></tr></thead>
              <tbody>
                {collectedRows.length === 0 ? <Empty cols={3} msg="No collections yet"/> : collectedRows.map(r => (
                  <tr key={r.id}>
                    <td><div className="flex items-center gap-2"><Avatar name={r.trainer_name ?? r.name ?? 'T'} size="xs"/><span className="font-medium text-gray-900">{r.trainer_name ?? r.name ?? '—'}</span></div></td>
                    <td className="font-bold">{formatCurrency(r.amount ?? 0)}</td>
                    <td className="text-gray-500 text-sm">{r.collected_at ?? r.created_at ? formatDate((r.collected_at ?? r.created_at)!) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </Card>
      ) : (
        <Card padding={false}>
          {cashQ.isLoading ? <Center/> : (
            <div className="overflow-x-auto">
            <table className="drivys-table w-full">
              <thead className="bg-gray-50"><tr><th>Trainer</th><th>Cash In Hand</th></tr></thead>
              <tbody>
                {cashRows.length === 0 ? <Empty cols={2} msg="No cash in hand"/> : cashRows.map(r => (
                  <tr key={r.id}>
                    <td><div className="flex items-center gap-2"><Avatar name={r.trainer_name ?? r.name ?? 'T'} size="xs"/><span className="font-medium text-gray-900">{r.trainer_name ?? r.name ?? '—'}</span></div></td>
                    <td className="font-bold">{formatCurrency(r.cash_in_hand ?? r.amount ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </Card>
      )}
    </div>
  )
}

const Center = () => <div className="flex justify-center py-12"><Loader2 className="animate-spin text-brand-500" size={24}/></div>
const Empty = ({cols,msg}:{cols:number;msg:string}) => <tr><td colSpan={cols} className="text-center text-gray-400 py-12 text-sm">{msg}</td></tr>
